import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { DynamicFormItem } from "./dynamicForm/dynamicFormModels";
import { RefDataItem } from "./refData/refDataModels";
import { Program } from "./program/programModels";
import { Session } from "./auth/authModels";
import { ProgramEvent } from "./programEvent/programEventModel";
import { ProgramSetting, ScheduledMaintenanceSupportInformation } from "../../MPP-Common-SPA/programSettings/models";
import { SystemSetting, ScheduledMaintenanceNotification } from "../../MPP-Common-SPA/systemSettings/models";
import { domainKey } from "../../MPP-Common-SPA/utils/globalUtility";
import { ProgramFeature } from "../../MPP-Common-SPA/featureFlags/models";
import { getLastVerificationStatus, setLastVerificationStatus, wasVerificationAttempted, handleVerificationOutcome } from '../../MPP-Common-SPA/utils/verificationNotificationUtils';

export const apiSliceTagTypes = {
  AuthTag: "Auth",
  RefDataTag: "RefData",
  DynamicFormTag: "DynamicForm",
  FormDefinitionTag: "FormDefinition",
  ProgramTag: "Program",
  ProgramEventsTag: "ProgramEvents",
  ProgramSettingsTag: "ProgramSettings",
  ApplicationTag: "Application",
  FieldMappingTag: "FieldMapping",
  ProgramFeatureMappingTag: "ProgramFeatureMapping",
  SystemSettingsTag: "SystemSettings",
  SettingsTag: "Settings"
}

const cacheTime = Number(process.env.REACT_APP_CACHE_TIME) || 3600000;
const {
  AuthTag,
  RefDataTag,
  DynamicFormTag,
  FormDefinitionTag,
  ProgramTag,
  ProgramEventsTag,
  ProgramSettingsTag,
  ApplicationTag,
  FieldMappingTag,
  ProgramFeatureMappingTag,
  SystemSettingsTag,
  SettingsTag,
} = apiSliceTagTypes;

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api",
    prepareHeaders: (headers, { getState }) => {
      headers.set("Content-Type", "application/json");
      headers.set("domain-key", domainKey);

      return headers;
    },
  }),
  keepUnusedDataFor: cacheTime / 1000,
  tagTypes: [
    AuthTag,
    RefDataTag,
    DynamicFormTag,
    FormDefinitionTag,
    ProgramTag,
    ProgramEventsTag,
    ProgramSettingsTag,
    ApplicationTag,
    FieldMappingTag,
    ProgramFeatureMappingTag,
    SystemSettingsTag,
    SettingsTag,
  ],
  endpoints: (build) => ({
    getSession: build.query<Session, void>({
      query: () => '/auth/session',
      providesTags: [AuthTag],
      keepUnusedDataFor: 0,
    
      async onQueryStarted(_, { queryFulfilled }) {
        const lastStatus = getLastVerificationStatus();
        const attempted = wasVerificationAttempted();
      
        try {
          const { data } = await queryFulfilled;
          const currentStatus = data?.user?.verificationStatus;

          handleVerificationOutcome(
            lastStatus,
            currentStatus,
            attempted
          );
      
          if (typeof currentStatus === 'number') {
            setLastVerificationStatus(currentStatus);
          }
        } catch {
          // intentionally silent
        }
      }
    }),
    getRefDataList: build.query<RefDataItem[], void>({
      query: () => "/lookup/references",
      providesTags: [RefDataTag],
    }),
    getDynamicFormList: build.query<DynamicFormItem[], void>({
      query: () => "/formDefinition/dynamicForms",
      providesTags: [DynamicFormTag],
    }),
    getProgram: build.query<Program, void>({
      query: () => "/program",
      providesTags: [ProgramTag],
    }),
    getProgramEventList: build.query<ProgramEvent[], string>({
      query: (programId) => ({
        url: `/programEvents`,
        params: { programId },
      }),
      providesTags: [ProgramEventsTag],
    }),
    getScheduledMaintenanceNotification: build.query<ScheduledMaintenanceNotification, void>({
      query: () => `/systemSettings/scheduledMaintenanceNotification`,
      providesTags: [SystemSettingsTag],
      transformResponse: (response: SystemSetting): ScheduledMaintenanceNotification => ({
        isEnabled: response.isEnabled,
        configuration: JSON.parse(response.configuration),
      }),
    }),
    getProgramSettingBySettingName: build.query<
      ProgramSetting,
      { settingName: string; programId: string }
    >({
      query: ({ settingName, programId }) => ({
        url: `/programSettings`,
        params: { settingName, programId },
      }),
      providesTags: [ProgramSettingsTag],
    }),
    getScheduledMaintenanceSupportInformation: build.query<ScheduledMaintenanceSupportInformation, void>({
      query: () => `/programSettings/scheduledMaintenanceSupportInformation`,
      providesTags: [ProgramSettingsTag],
      transformResponse: (response: ProgramSetting): ScheduledMaintenanceSupportInformation => ({
        isEnabled: response.isEnabled,
        configuration: JSON.parse(response.configuration),
      }),
    }),
    getEvent: build.query<ProgramEvent, string>({
      query: (id) => `/programEvents/${id}`,
      providesTags: [ProgramEventsTag],
    }),
    getFieldMapping: build.query<any, string>({
      query: () => `/program/fieldMapping`,
      providesTags: [FieldMappingTag],
    }),
    GetProgramFeatureMappingByName: build.query<
      ProgramFeature,
      { featureName: string; programId: string }
    >({
      query: ({ featureName, programId }) => ({
        url: `/programFeatureMapping`,
        params: { featureName, programId },
      }),
      providesTags: [ProgramFeatureMappingTag],
    }),
    GetProgramFeatures: build.query<
      Record<string, boolean>,
      string
    >({
      query: (programId) => `/programFeatureMapping/program/${programId}`,
      providesTags: [ProgramFeatureMappingTag],
    }),
  }),
});

export const {
  useGetSessionQuery,
  useGetRefDataListQuery,
  useGetDynamicFormListQuery,
  useGetProgramQuery,
  useGetProgramEventListQuery,
  useGetProgramSettingBySettingNameQuery,
  useGetEventQuery,
  useGetFieldMappingQuery,
  useGetProgramFeatureMappingByNameQuery,
  useGetProgramFeaturesQuery,
  useGetScheduledMaintenanceNotificationQuery,
  useGetScheduledMaintenanceSupportInformationQuery
} = apiSlice;
